import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isBefore,
  isAfter,
  isToday,
  parseISO,
} from 'date-fns'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DateRange {
  start: string // YYYY-MM-DD
  end: string   // YYYY-MM-DD
}

interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  placeholder?: string
  className?: string
  maxDate?: Date
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function MonthCalendar({
  month,
  selectedStart,
  selectedEnd,
  hoverDate,
  onDayClick,
  onDayHover,
  onMonthChange,
  showNavLeft,
  showNavRight,
  maxDate,
}: {
  month: Date
  selectedStart: Date | null
  selectedEnd: Date | null
  hoverDate: Date | null
  onDayClick: (date: Date) => void
  onDayHover: (date: Date | null) => void
  onMonthChange: (direction: 'prev' | 'next') => void
  showNavLeft: boolean
  showNavRight: boolean
  maxDate?: Date
}) {
  const monthStart = startOfMonth(month)
  const monthEnd = endOfMonth(month)
  const calStart = startOfWeek(monthStart)
  const calEnd = endOfWeek(monthEnd)
  const days = eachDayOfInterval({ start: calStart, end: calEnd })

  // Effective end for range highlight — use hover if no end selected yet
  const rangeEnd = selectedEnd ?? (selectedStart && hoverDate ? hoverDate : null)
  const rangeStart = selectedStart

  const isInRange = (day: Date) => {
    if (!rangeStart || !rangeEnd) return false
    const s = isBefore(rangeStart, rangeEnd) ? rangeStart : rangeEnd
    const e = isBefore(rangeStart, rangeEnd) ? rangeEnd : rangeStart
    return isWithinInterval(day, { start: s, end: e })
  }

  const isRangeStart = (day: Date) => {
    if (!rangeStart || !rangeEnd) return false
    return isSameDay(day, isBefore(rangeStart, rangeEnd) ? rangeStart : rangeEnd)
  }

  const isRangeEnd = (day: Date) => {
    if (!rangeStart || !rangeEnd) return false
    return isSameDay(day, isBefore(rangeStart, rangeEnd) ? rangeEnd : rangeStart)
  }

  const isDisabled = (day: Date) => {
    if (maxDate && isAfter(day, maxDate)) return true
    return false
  }

  return (
    <div className="select-none w-[252px]">
      {/* Month header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onMonthChange('prev')}
          className={cn(
            'h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
            !showNavLeft && 'invisible'
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {format(month, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          onClick={() => onMonthChange('next')}
          className={cn(
            'h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
            !showNavRight && 'invisible'
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d) => (
          <div key={d} className="h-8 flex items-center justify-center text-[11px] font-medium text-muted-foreground">
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inRange = isInRange(day)
          const isStart = isRangeStart(day)
          const isEnd = isRangeEnd(day)
          const isSelected = !!(selectedStart && isSameDay(day, selectedStart)) || !!(selectedEnd && isSameDay(day, selectedEnd))
          const disabled = isDisabled(day)
          const outsideMonth = !isSameMonth(day, month)
          const today = isToday(day)

          return (
            <div
              key={day.toISOString()}
              className={cn(
                'relative h-8 flex items-center justify-center',
                // Range background — extends across full cell
                inRange && !isStart && !isEnd && 'bg-primary/10',
                // Start cap
                isStart && 'bg-primary/10 rounded-l-full',
                // End cap
                isEnd && 'bg-primary/10 rounded-r-full',
                // Single selected (start with no end yet)
                isSelected && !selectedEnd && 'rounded-full',
              )}
            >
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onDayClick(day)}
                onMouseEnter={() => !disabled && onDayHover(day)}
                onMouseLeave={() => onDayHover(null)}
                className={cn(
                  'h-7 w-7 rounded-full text-sm flex items-center justify-center transition-colors z-10',
                  // Default
                  !isSelected && !isStart && !isEnd && 'hover:bg-accent hover:text-accent-foreground',
                  // Outside month
                  outsideMonth && 'text-muted-foreground/30',
                  // Today indicator
                  today && !isSelected && !isStart && !isEnd && 'font-semibold text-primary',
                  // Selected / range endpoints
                  (isSelected || isStart || isEnd) && 'bg-primary text-primary-foreground font-semibold hover:bg-primary',
                  // Disabled
                  disabled && 'opacity-30 cursor-not-allowed pointer-events-none',
                )}
              >
                {format(day, 'd')}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Select date range',
  className,
  maxDate,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [leftMonth, setLeftMonth] = useState<Date>(() => {
    if (value.start) return startOfMonth(parseISO(value.start))
    return startOfMonth(new Date())
  })
  const [hoverDate, setHoverDate] = useState<Date | null>(null)
  // Tracks first click (start) before end is picked
  const [pendingStart, setPendingStart] = useState<Date | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)

  const rightMonth = addMonths(leftMonth, 1)

  const selectedStart = value.start ? parseISO(value.start) : null
  const selectedEnd = value.end ? parseISO(value.end) : null

  // Determine what to show as "in-flight" start during selection
  const activeStart = pendingStart ?? selectedStart
  const activeEnd = pendingStart ? null : selectedEnd

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setPendingStart(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setPendingStart(null)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const handleDayClick = useCallback((date: Date) => {
    if (!pendingStart) {
      // First click — record start locally, do NOT notify parent yet
      setPendingStart(date)
    } else {
      // Second click — complete the range and notify parent once
      const start = isBefore(pendingStart, date) ? pendingStart : date
      const end = isBefore(pendingStart, date) ? date : pendingStart
      onChange({
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      })
      setPendingStart(null)
      setOpen(false)
    }
  }, [pendingStart, onChange])

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange({ start: '', end: '' })
    setPendingStart(null)
  }

  const hasValue = !!(value.start || value.end)

  const displayText = () => {
    if (pendingStart) {
      return `${format(pendingStart, 'MMM d, yyyy')} → pick end date`
    }
    if (value.start && value.end) {
      return `${format(parseISO(value.start), 'MMM d, yyyy')} → ${format(parseISO(value.end), 'MMM d, yyyy')}`
    }
    if (value.start) {
      return format(parseISO(value.start), 'MMM d, yyyy')
    }
    return null
  }

  const text = displayText()

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger input */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'hover:bg-accent/50 transition-colors cursor-pointer',
          open && 'ring-2 ring-ring ring-offset-2',
        )}
      >
        <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className={cn(
          'flex-1 text-left truncate',
          !text && 'text-muted-foreground',
          pendingStart && 'text-muted-foreground italic',
        )}>
          {text ?? placeholder}
        </span>
        {hasValue && !pendingStart && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={(e) => e.key === 'Enter' && handleClear(e as unknown as React.MouseEvent)}
            className="h-4 w-4 rounded-sm flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0"
            aria-label="Clear date range"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div className={cn(
          'absolute top-full mt-2 z-50 rounded-xl border bg-popover shadow-elevation-2 p-4',
          'animate-in fade-in-0 zoom-in-95',
          // Responsive: side by side on md+, stacked on mobile
          'flex flex-col md:flex-row gap-4',
          // Align right on smaller screens to avoid overflow
          'right-0 md:left-0 md:right-auto',
        )}>
          {/* Selection hint */}
          <div className="absolute -top-px left-0 right-0 flex justify-center pointer-events-none">
            <span className="bg-primary text-primary-foreground text-[10px] font-medium px-2 py-0.5 rounded-b-md">
              {pendingStart ? 'Click to select end date' : 'Click to select start date'}
            </span>
          </div>

          <MonthCalendar
            month={leftMonth}
            selectedStart={activeStart}
            selectedEnd={activeEnd}
            hoverDate={pendingStart ? hoverDate : null}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onMonthChange={(dir) =>
              setLeftMonth(dir === 'prev' ? subMonths(leftMonth, 1) : addMonths(leftMonth, 1))
            }
            showNavLeft={true}
            showNavRight={false}
            maxDate={maxDate}
          />

          <div className="border-t md:border-t-0 md:border-l border-border" />

          <MonthCalendar
            month={rightMonth}
            selectedStart={activeStart}
            selectedEnd={activeEnd}
            hoverDate={pendingStart ? hoverDate : null}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onMonthChange={(dir) =>
              setLeftMonth(dir === 'prev' ? subMonths(leftMonth, 1) : addMonths(leftMonth, 1))
            }
            showNavLeft={false}
            showNavRight={true}
            maxDate={maxDate}
          />
        </div>
      )}
    </div>
  )
}
